/** Pure canonical-path helpers for the batch tenant-config grid.
 *
 * A CONCRETE path addresses one value in one tenant's config and uses
 * `segment` / `segment[N]` grammar ("auth_methods[0].domain"). A CANONICAL
 * key identifies a grid row and replaces every index with `[*]`
 * ("auth_methods[*].domain"). No React imports — everything here is
 * side-effect-free and unit-testable.
 */
import type { SchemaProp } from '../config/schema-grid/types';

export type CanonicalSegment =
  | { kind: 'key'; name: string }
  | { kind: 'index'; index: number | '*' };

/** Parse a canonical/concrete path into typed segments. Accepts both `[N]`
 *  and `[*]` index segments ("a.b[0].c" → key/key/index/key). Config keys
 *  never contain `[`, so a bracket unambiguously starts an index group.
 *  Malformed paths (empty segments, bad bracket groups) throw. */
export function parseCanonicalPath(path: string): CanonicalSegment[] {
  const segments: CanonicalSegment[] = [];
  for (const part of path.split('.')) {
    const bracket = part.indexOf('[');
    const name = bracket === -1 ? part : part.slice(0, bracket);
    const rest = bracket === -1 ? '' : part.slice(bracket);
    if (!name && !rest) {
      throw new Error(`parseCanonicalPath: empty segment in "${path}"`);
    }
    if (name) segments.push({ kind: 'key', name });
    if (rest) {
      if (!/^(\[(\d+|\*)\])+$/.test(rest)) {
        throw new Error(`parseCanonicalPath: malformed segment "${part}" in "${path}"`);
      }
      for (const m of rest.matchAll(/\[(\d+|\*)\]/g)) {
        const raw = m[1] ?? '';
        segments.push({ kind: 'index', index: raw === '*' ? '*' : Number(raw) });
      }
    }
  }
  return segments;
}

function serializeSegments(segments: CanonicalSegment[]): string {
  let out = '';
  for (const seg of segments) {
    if (seg.kind === 'key') {
      out = out ? `${out}.${seg.name}` : seg.name;
    } else {
      out += `[${seg.index}]`;
    }
  }
  return out;
}

/** Canonicalize a concrete path: every `[N]` becomes `[*]`
 *  ("auth_methods[0].domain" → "auth_methods[*].domain"). */
export function canonicalize(concretePath: string): string {
  return serializeSegments(
    parseCanonicalPath(concretePath).map((seg) =>
      seg.kind === 'index' ? { kind: 'index', index: '*' } : seg,
    ),
  );
}

/** Does the user's key query match a canonical row key?
 *
 *  Plain queries (no `.`, no `[`) are a case-insensitive substring match on
 *  the canonical key OR the description. Path-shaped queries are parsed
 *  (indices normalized to "any index") and matched as a CONTIGUOUS run of
 *  the key's segments — key-segment names match by substring, an index query
 *  segment matches any index segment. Malformed path-ish queries fall back
 *  to a plain substring match on the canonical key. */
export function matchesKeyQuery(query: string, canonicalKey: string, description: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const keyLc = canonicalKey.toLowerCase();
  if (!q.includes('.') && !q.includes('[')) {
    return keyLc.includes(q) || description.toLowerCase().includes(q);
  }
  let querySegs: CanonicalSegment[];
  let keySegs: CanonicalSegment[];
  try {
    querySegs = parseCanonicalPath(q);
    keySegs = parseCanonicalPath(keyLc);
  } catch {
    return keyLc.includes(q);
  }
  if (querySegs.length === 0 || querySegs.length > keySegs.length) return false;
  for (let start = 0; start + querySegs.length <= keySegs.length; start += 1) {
    let ok = true;
    for (let i = 0; i < querySegs.length; i += 1) {
      const qs = querySegs[i];
      const ks = keySegs[start + i];
      if (qs === undefined || ks === undefined) {
        ok = false;
        break;
      }
      if (qs.kind === 'key') {
        if (ks.kind !== 'key' || !ks.name.includes(qs.name)) {
          ok = false;
          break;
        }
      } else if (ks.kind !== 'index') {
        ok = false;
        break;
      }
      // index vs index always matches — `[N]` in a query means "any index".
    }
    if (ok) return true;
  }
  return false;
}

// ---------------------------------------------------------------------------
// Schema flattening
// ---------------------------------------------------------------------------

export interface SchemaLeaf {
  /** Canonical key (may contain `[*]`). */
  key: string;
  schema: SchemaProp;
  group: string;
}

function hasProperties(props: Record<string, SchemaProp> | undefined): props is Record<string, SchemaProp> {
  return props !== undefined && Object.keys(props).length > 0;
}

/** Flatten a schema's `properties` map into canonical leaf rows.
 *
 *  - object with `properties` → recurse (dot-joined)
 *  - object without `properties` (or only `additionalProperties`) → one leaf
 *  - array with `items.properties` → recurse as `key[*].child` leaves
 *  - array otherwise → one leaf
 *  - any prop with `x-widget` → one leaf (never recursed)
 *  - keys starting with `_cp_` and the key `accounts` are skipped */
export function flattenSchemaLeaves(
  props: Record<string, SchemaProp>,
  group: string,
): SchemaLeaf[] {
  const leaves: SchemaLeaf[] = [];
  const walk = (prefix: string, level: Record<string, SchemaProp>): void => {
    for (const [key, prop] of Object.entries(level)) {
      if (key.startsWith('_cp_') || key === 'accounts') continue;
      const path = prefix ? `${prefix}.${key}` : key;
      if (prop['x-widget']) {
        leaves.push({ key: path, schema: prop, group });
        continue;
      }
      const childProps = prop.properties;
      if (prop.type === 'object' && hasProperties(childProps)) {
        walk(path, childProps);
        continue;
      }
      const itemProps = prop.items?.properties;
      if (prop.type === 'array' && hasProperties(itemProps)) {
        walk(`${path}[*]`, itemProps);
        continue;
      }
      leaves.push({ key: path, schema: prop, group });
    }
  };
  walk('', props);
  return leaves;
}

// ---------------------------------------------------------------------------
// Config flattening + canonical resolution
// ---------------------------------------------------------------------------

export interface ConfigLeaf {
  concretePath: string;
  canonicalKey: string;
  value: unknown;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

/** Flatten one tenant's config into concrete leaves.
 *
 *  Plain objects recurse (dot-joined); arrays where EVERY element is a plain
 *  object recurse with `[i]` segments. Scalars, arrays of scalars/mixed, and
 *  empty objects/arrays are leaves. `_cp_*` keys are skipped at any depth.
 *
 *  `accounts` keys are skipped at ANY depth (v1 simplification): the only
 *  real occurrence is the restech named-accounts container directly under a
 *  top-level integration key (`config[restech].accounts`), which the batch
 *  grid deliberately excludes (the backend rejects account paths). Skipping
 *  at every depth is a conservative superset of that rule and avoids
 *  depth-tracking for a key that never legitimately appears elsewhere. */
export function flattenConfigLeaves(config: Record<string, unknown>): ConfigLeaf[] {
  const leaves: ConfigLeaf[] = [];
  const walkObject = (obj: Record<string, unknown>, concrete: string, canonical: string): void => {
    for (const [key, value] of Object.entries(obj)) {
      if (key.startsWith('_cp_') || key === 'accounts') continue;
      walkNode(
        value,
        concrete ? `${concrete}.${key}` : key,
        canonical ? `${canonical}.${key}` : key,
      );
    }
  };
  const walkNode = (value: unknown, concrete: string, canonical: string): void => {
    if (isPlainObject(value)) {
      if (Object.keys(value).length === 0) {
        leaves.push({ concretePath: concrete, canonicalKey: canonical, value });
      } else {
        walkObject(value, concrete, canonical);
      }
      return;
    }
    if (Array.isArray(value) && value.length > 0 && value.every(isPlainObject)) {
      value.forEach((element, i) => {
        walkNode(element, `${concrete}[${i}]`, `${canonical}[*]`);
      });
      return;
    }
    leaves.push({ concretePath: concrete, canonicalKey: canonical, value });
  };
  walkObject(config, '', '');
  return leaves;
}

/** Resolve a canonical key against one tenant's config, fanning `[*]` out
 *  over every array index (`[N]` resolves that index only). Missing → `[]`. */
export function resolveCanonical(
  config: Record<string, unknown>,
  canonicalKey: string,
): Array<{ concretePath: string; value: unknown }> {
  let segments: CanonicalSegment[];
  try {
    segments = parseCanonicalPath(canonicalKey);
  } catch {
    return [];
  }
  const out: Array<{ concretePath: string; value: unknown }> = [];
  const walk = (node: unknown, idx: number, concrete: string): void => {
    const seg = segments[idx];
    if (seg === undefined) {
      out.push({ concretePath: concrete, value: node });
      return;
    }
    if (seg.kind === 'key') {
      if (!isPlainObject(node) || !(seg.name in node)) return;
      walk(node[seg.name], idx + 1, concrete ? `${concrete}.${seg.name}` : seg.name);
      return;
    }
    if (!Array.isArray(node)) return;
    if (seg.index === '*') {
      node.forEach((element, i) => walk(element, idx + 1, `${concrete}[${i}]`));
      return;
    }
    if (seg.index < 0 || seg.index >= node.length) return;
    walk(node[seg.index], idx + 1, `${concrete}[${seg.index}]`);
  };
  walk(config, 0, '');
  return out;
}
